/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { resolveDlpUserMessage } from '@adp-common/chat-uikit-shared';
import { I18n } from '@adp-arch/i18n';
import { Toast } from '@adp-arch/bot-semi';

import { useChatAreaStoreSet } from '../context/use-chat-area-context';
import { isFileSizeNotExceed } from '../../utils/file-validation';
import { FileStatus, FileType } from '../../store/types';
import { useUploadController } from '../../context/upload-controller-context';
import { usePreference } from '../../context/preference';
import {
  getFileSizeLimitText,
  getFileSizeReachLimitI18n,
  MAX_UPLOAD_PROGRESS,
} from '../../constants/file';

const useValidateUploadFile = () => {
  const { fileSizeLimit } = usePreference();

  return (file: File) => {
    if (isFileSizeNotExceed(file, fileSizeLimit)) {
      return true;
    }

    Toast.warning({
      content: getFileSizeReachLimitI18n({
        limitText: getFileSizeLimitText(fileSizeLimit),
      }),
      showClose: false,
    });
    return false;
  };
};

const useUpload = () => {
  const { useBatchFileUploadStore, useSenderInfoStore } = useChatAreaStoreSet();

  const { createControllerAndUpload, cancelUploadById } = useUploadController();
  const { uploadBotId } = usePreference();
  const userInfo = useSenderInfoStore(state => state.userInfo);
  return (fileId: string, file: File) => {
    if (!userInfo) {
      throw new Error('failed to get user info');
    }

    const { immerUpdateFileDataById } = useBatchFileUploadStore.getState();
    createControllerAndUpload({
      fileId,
      file,
      userId: userInfo.id,
      botId: uploadBotId,
      onReady: (_e, id) => {
        immerUpdateFileDataById(id, data => {
          data.status = FileStatus.Uploading;
        });
      },
      onProgress: (event, id) => {
        immerUpdateFileDataById(id, data => {
          data.percent = event.percent;
        });
      },
      onComplete: (event, id) => {
        const { uploadResult } = event;
        const notice = resolveDlpUserMessage(event, I18n.language);
        if (notice?.kind === 'fail_open') {
          Toast.warning({ content: notice.message, showClose: true });
        }
        immerUpdateFileDataById(id, data => {
          data.status = FileStatus.Success;
          data.percent = MAX_UPLOAD_PROGRESS;
          data.errorMessage = undefined;
          data.errorReasonCode = undefined;
          const uri = uploadResult.Uri;

          if (!uri) {
            data.status = FileStatus.Error;
            throw new Error('upload complete without uri');
          }

          data.uri = uri;

          if (data.fileType !== FileType.Image) {
            return;
          }

          data.meta = {
            width: uploadResult.ImageWidth ?? 0,
            height: uploadResult.ImageHeight ?? 0,
          };
        });
      },
      onError: (event, id) => {
        const rejection = resolveDlpUserMessage(event, I18n.language);
        const blocked = rejection && rejection.kind !== 'fail_open' ? rejection : undefined;
        if (blocked) {
          Toast.error({ content: blocked.message, showClose: true });
        }
        immerUpdateFileDataById(id, data => {
          data.status = FileStatus.Error;
          data.errorMessage = blocked?.message;
          data.errorReasonCode = blocked?.reasonCode;
        });
        cancelUploadById(id);
      },
    });
  };
};

export const useCreateFileAndUpload = () => {
  const { useBatchFileUploadStore } = useChatAreaStoreSet();
  const upload = useUpload();
  const validateUploadFile = useValidateUploadFile();
  return (fileId: string, file: File) => {
    if (!validateUploadFile(file)) {
      return;
    }

    const { immerCreateFileData } = useBatchFileUploadStore.getState();
    immerCreateFileData(fileId, file);
    upload(fileId, file);
  };
};

export const useRetryUpload = () => {
  const upload = useUpload();
  const { useBatchFileUploadStore } = useChatAreaStoreSet();
  const validateUploadFile = useValidateUploadFile();
  return (fileId: string, file: File) => {
    if (!validateUploadFile(file)) {
      return;
    }

    const { immerUpdateFileDataById } = useBatchFileUploadStore.getState();
    immerUpdateFileDataById(fileId, state => {
      state.percent = 0;
      state.status = FileStatus.Init;
      state.errorMessage = undefined;
      state.errorReasonCode = undefined;
    });
    upload(fileId, file);
  };
};
